import type { CartItem } from '@/lib/cartStore';
import {
  evaluateCatalogLine,
  type CatalogProductSnap,
} from '@/lib/catalogStock';

export type CartProductSnap = CatalogProductSnap;

export type CartLineIssue =
  | { ok: true }
  | { ok: false; message: string; recoverable: boolean };

/**
 * `null` means product not found (or failed to load). `recoverable` marks a stock shortage
 * (out of stock / insufficient quantity) — which the checkout can resolve via a recovery-rule
 * substitution — as opposed to a removed/inactive product, which cannot.
 */
export function getCartLineIssue(
  item: CartItem,
  snap: CartProductSnap | null,
  t: (key: string, values?: Record<string, number | string>) => string,
): CartLineIssue {
  const code = evaluateCatalogLine(snap, item.variantId, item.quantity);
  if (code === 'ok') return { ok: true };

  switch (code) {
    case 'removed':
      return { ok: false, recoverable: false, message: t('checkout.cartProductRemoved') };
    case 'inactive':
    case 'unavailable_variant':
      return { ok: false, recoverable: false, message: t('product.unavailable') };
    case 'out_of_stock':
      return { ok: false, recoverable: true, message: t('product.outOfStock') };
    case 'insufficient_variant': {
      const v =
        snap != null ? (snap.variants ?? []).find((x) => x.id === item.variantId) : undefined;
      return {
        ok: false,
        recoverable: true,
        message: t('checkout.onlyNInStock', { count: v?.stock ?? 0 }),
      };
    }
    case 'insufficient_product':
      return {
        ok: false,
        recoverable: true,
        message: t('checkout.onlyNInStock', { count: snap?.stock ?? 0 }),
      };
    default:
      return { ok: true };
  }
}
